import { css, keyframes } from "@emotion/react";
import React, { forwardRef } from "react";
import { AsComponentProp, ComponentWithAs } from "../AsComponentProps";
import { FieldGroupContextValue, useFieldGroup } from "../FieldGroup";
import { PartialColorProps, useTheme } from "../theme";
import { ShimmerEffect } from "../ShimmerEffect";
import { useClickAnimation } from "../hooks/useClickAnimation";

const fade = keyframes`
  100% {
      opacity: 0;
  }
`;
const wave = keyframes`
  100% {
      box-shadow: 0 0 0 6px currentcolor;
  }
`;

export const resetButtonStyle = css`
  position: relative;
  box-sizing: border-box;
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  padding: 0px;
  margin: 0px;
  font: inherit;
  color: inherit;
  text-align: center;
  text-decoration: none;
  white-space: nowrap;
  touch-action: manipulation;
  cursor: pointer;
  user-select: none;
  background-color: transparent;
  border: none;
  appearance: none;
  &:disabled {
    cursor: not-allowed;
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

export interface BaseButtonProps
  extends PartialColorProps, FieldGroupContextValue {
  children?: React.ReactNode;
  /**
   * ボタンの見た目を指定します。
   *
   * @default text
   */
  variant?: "filled" | "outlined" | "text";
  /**
   * アイコンとして入れる要素を指定します。
   *
   * @default null
   */
  icon?: React.ReactNode;
  /**
   * 文字列の後ろに入れるアイコンの要素を指定します。
   * iconが指定されておらず、childrenがある場合のみ表示できます。
   *
   * @default null
   */
  endIcon?: React.ReactNode;
  /**
   * ボタンに丸みをつけます
   *
   * @default false
   */
  rounded?: boolean;
  /**
   * 最大限幅を大きくします。
   *
   * @default false
   */
  fullWidth?: boolean;
  /**
   * Use readOnly instead
   */
  disabled?: boolean;
  /**
   * 大きさを指定します。
   *
   * @default medium
   */
  size?: "small" | "medium" | "large" | "xlarge";
}
/**
## 仕様について

### カラーリング

Button要素に対して親のカラーを継承したいという需要が理解できなかったため、
デフォルトの挙動はinheritではなく、Themeの`palette.text.primary`をメインカラーをとしたものになります。

### Icon

近年、Floating Action Button (FAB)に代表されるアイコンのみのボタンが多く用いられるようになってきました。
このコンポーネントでもその需要に対応するため、icon属性を導入しています。

子要素を指定せずicon属性を指定した場合、Button要素はrounded属性の有無によって正円または正方形になります。
rounded属性を指定した場合のButton要素はFAB相当です。

また子要素が指定された場合、いわゆるExpanded FABと言われるコンポーネントと同等の見た目になります。
このときicon属性に指定した要素の右側には自動でマージンが入ります。


## Button要素のパターンを増やしたい場合

variant属性は見た目のみを変化させるべきです。variantの値によって色や形状を変化させてはいけません。

rounded属性は唯一形状を変化させます。ですが属性による形状の変化はそれが同一のコンポーネントである限り最低限であるべきです。
もしrounded以外に形状を変化させる属性を追加したいと思ったなら、別コンポーネントとして切り出せないか検討してください。
 */

const defaultElement: keyof React.JSX.IntrinsicElements = "button";

/*
export type ButtonProps<E> = E extends
    | keyof JSX.IntrinsicElements
    | React.ComponentType<any>
    ? MergeElementProps<E, BaseButtonProps> & {
          as: E
      }
    : E extends never
    ? MergeElementProps<"button", BaseButtonProps>
    : never
*/

export type ButtonProps<E extends React.ElementType = "button"> =
  AsComponentProp<E, BaseButtonProps>;

export const Button = forwardRef(
  <E extends React.ElementType = "button">(
    {
      icon,
      endIcon,
      children,
      as,
      onClick: propsOnClick,
      variant = "text",
      size = "medium",
      color = "default",
      rounded = false,
      fullWidth,
      loading: propsLoading,
      readOnly: propsReadOnly,
      disabled,
      ...props
    }: ButtonProps<E>,
    ref: React.ForwardedRef<E>,
  ) => {
    const Component: React.ElementType = as ?? defaultElement;
    const { clicked, onClick, onAnimationEnd } =
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      useClickAnimation(propsOnClick);
    const theme = useTheme();
    /**
     * flags
     */
    const hasChildren = children !== undefined && children !== null;
    const hasIcon = icon !== undefined && icon !== null;
    const hasEndIcon = endIcon !== undefined && endIcon !== null;
    const onlyIcon = hasIcon && hasChildren === false;
    const expanded = hasIcon && hasChildren;
    if ((hasEndIcon && hasChildren === false) || (onlyIcon && hasEndIcon)) {
      throw new Error("endIcon can only be used when children is specified.");
    }
    /**
     * loading
     */
    const { loading, readOnly } = useFieldGroup({
      loading: propsLoading,
      readOnly: propsReadOnly,
    });
    return (
      <ShimmerEffect active={loading}>
        <Component
          css={[
            resetButtonStyle,
            css`
              border: 1px solid transparent;
              outline: 0;
              transition:
                background-color ${theme.transitions.duration.shortest}
                  ${theme.transitions.easing.easeIn},
                border-color ${theme.transitions.duration.shortest}
                  ${theme.transitions.easing.easeIn};
              &:hover {
                transition: none;
              }
              &::selection {
                color: inherit;
                background-color: transparent;
              }
              &:disabled {
                color: ${theme.palette.action.disabled};
                text-shadow: none;
              }
              &::after {
                position: absolute;
                top: -1px;
                right: -1px;
                bottom: -1px;
                left: -1px;
                z-index: 1;
                display: block;
                color: ${color === "default"
                  ? theme.palette.gray[600]
                  : theme.palette.intentions[color].main};
                pointer-events: none;
                content: "";
                border-radius: inherit;
                opacity: 0.2;
                transform: scale(1);
              }
              &[data-clicked="true"]::after {
                animation:
                  ${fade} 2s cubic-bezier(0.08, 0.82, 0.17, 1),
                  ${wave} 0.4s cubic-bezier(0.08, 0.82, 0.17, 1);
              }
            `,
            variant === "filled"
              ? css`
                  box-shadow: ${theme.shadows[1]};
                  &:hover {
                    box-shadow: ${theme.shadows[3]};
                  }
                  &:disabled {
                    color: ${theme.palette.action.disabled};
                    background-color: ${theme.palette.action
                      .disabledBackground};
                    border-color: ${theme.palette.action.disabledBackground};
                    box-shadow: none;
                  }
                `
              : null,
            variant === "filled"
              ? color === "default"
                ? css`
                    color: ${theme.palette.text.primary};
                    background-color: ${theme.palette.gray[50]};
                    border-color: ${theme.palette.gray[50]};
                    &:active {
                      background-color: ${theme.palette.gray[100]};
                      border-color: ${theme.palette.gray[100]};
                    }
                  `
                : css`
                    color: ${theme.palette.intentions[color].contrastText};
                    background-color: ${theme.palette.intentions[color].main};
                    border-color: ${theme.palette.intentions[color].main};
                    &:active {
                      background-color: ${theme.palette.intentions[color].dark};
                      border-color: ${theme.palette.intentions[color].dark};
                    }
                  `
              : null,
            variant === "outlined"
              ? css`
                  &:disabled {
                    color: ${theme.palette.action.disabled};
                    border-color: ${theme.palette.action.disabledBackground};
                  }
                  &::before {
                    position: absolute;
                    display: block;
                    width: 100%;
                    height: 100%;
                    pointer-events: none;
                    content: "";
                    background-color: ${color === "default"
                      ? theme.palette.gray[600]
                      : theme.palette.intentions[color].main};
                    border-radius: inherit;
                    opacity: 0;
                    transition: opacity ${theme.transitions.duration.shortest}
                      ${theme.transitions.easing.easeIn};
                  }

                  &:hover::before {
                    opacity: 0.08;
                  }
                `
              : null,
            variant === "outlined"
              ? color === "default"
                ? css`
                    color: ${theme.palette.text.primary};
                    background-color: white;
                    border-color: ${theme.palette.action.disabled};
                    &:active {
                      border-color: ${theme.palette.gray[500]};
                    }
                  `
                : css`
                    color: ${theme.palette.intentions[color].main};
                    background-color: ${theme.palette.intentions[color]
                      .contrastText};
                    border-color: ${theme.palette.intentions[color].main};
                    &:active {
                      border-color: ${theme.palette.intentions[color].dark};
                    }
                  `
              : null,
            variant === "text"
              ? css`
                  text-decoration: underline;
                  transition-property: background-color;
                  &:disabled {
                    color: ${theme.palette.text.disabled};
                  }
                  @media (any-hover: hover) {
                    &:hover {
                      background-color: ${theme.palette.action.hover};
                    }
                  }
                  @media not all and (any-hover: hover) {
                    &[data-clicked="true"] {
                      background-color: ${theme.palette.action.hover};
                    }
                  }
                `
              : null,
            variant === "text"
              ? color === "default"
                ? css`
                    color: ${theme.palette.text.primary};
                  `
                : css`
                    color: ${theme.palette.intentions[color].main};
                  `
              : null,
            theme.typography.button,
            size === "small"
              ? {
                  fontSize: theme.typography.button.fontSize * 0.8,
                  height: 32,
                  minHeight: 32,
                  padding: "4px 10px",
                  borderRadius: rounded ? 16 : 4,
                }
              : size === "medium"
                ? {
                    height: 42,
                    minHeight: 42,
                    padding: `4px 16px`,
                    borderRadius: rounded ? 21 : 4,
                  }
                : size === "large"
                  ? {
                      fontSize: theme.typography.button.fontSize * 1.1,
                      height: 48,
                      minHeight: 48,
                      padding: `6px 22px`,
                      borderRadius: rounded ? 24 : 4,
                    }
                  : {
                      fontSize: theme.typography.button.fontSize * 1.1,
                      height: 56,
                      minHeight: 56,
                      padding: `6px 22px`,
                      borderRadius: rounded ? 28 : 4,
                    },
            onlyIcon
              ? size === "small"
                ? {
                    width: 32,
                    padding: 0,
                  }
                : size === "medium"
                  ? {
                      width: 42,
                      padding: 0,
                    }
                  : size === "large"
                    ? {
                        width: 48,
                        padding: 0,
                      }
                    : {
                        width: 56,
                        padding: 0,
                      }
              : null,
            fullWidth &&
              css`
                flex: 1 1 100%;
                width: 100%;
                max-width: 100%;
              `,
          ]}
          ref={ref}
          {...props}
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          disabled={disabled || readOnly}
          onClick={onClick}
          onAnimationEnd={onAnimationEnd}
          data-clicked={clicked}
        >
          {hasIcon ? (
            <span
              css={[
                expanded
                  ? {
                      marginRight: size === "small" ? 4 : 8,
                    }
                  : null,
                css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                `,
              ]}
            >
              {icon}
            </span>
          ) : null}
          {children}
          {hasEndIcon ? (
            <span
              css={[
                {
                  marginLeft: size === "small" ? 4 : 8,
                },
                css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                `,
              ]}
            >
              {endIcon}
            </span>
          ) : null}
        </Component>
      </ShimmerEffect>
    );
  },
) as ComponentWithAs<"button", BaseButtonProps>;
